import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// ActionsTable renders a list of analyser actions with an Apply
// button per row. Accepts a `columns` descriptor so each analyser
// can show the fields it cares about (band, current parent, RSSI,
// power saving, …) without forking the table component.
//
// Each column = { key, label, render(action) }.
//
// Actions are expected to carry the standard {endpoint, method,
// payload} fields so executor delegation is generic.
const ActionsTable = ({
  actions,
  columns,
  onApply,
  busyActionId,
  emptyText = 'No actions suggested.',
  applyAllLabel = 'Apply all',
  onApplyAll,
  applyAllBusy = false,
  applyAllDisabled = false,
  applyAllDisabledTooltip = 'Already applied for this result. Run analysis again to re-apply.',
}) => {
  const [showJSON, setShowJSON] = useState(null);

  const safeActions = useMemo(() => actions || [], [actions]);

  if (safeActions.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {emptyText}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      {onApplyAll && (
        <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`${safeActions.length} actions`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip
            title={
              applyAllDisabled && !applyAllBusy
                ? applyAllDisabledTooltip
                : 'Apply every action in order; stops on first error.'
            }
          >
            <span>
              <Button
                variant="contained"
                size="small"
                startIcon={applyAllBusy ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                onClick={onApplyAll}
                disabled={applyAllBusy || applyAllDisabled}
              >
                {applyAllLabel}
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}
      <TableContainer sx={{ maxHeight: 480 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.key} sx={{ fontWeight: 600 }}>
                  {col.label}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ fontWeight: 600, width: 140 }}>
                Apply
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {safeActions.map((action, idx) => {
              const id = action.action_id || action.id || `${action.serial || 'row'}-${idx}`;
              const busy = busyActionId === id;
              const expanded = showJSON === id;
              return (
                <React.Fragment key={id}>
                  <TableRow hover>
                    {columns.map((col) => (
                      <TableCell key={col.key}>
                        {col.render(action)}
                      </TableCell>
                    ))}
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title={expanded ? 'Hide payload' : 'Show payload'}>
                          <IconButton
                            size="small"
                            onClick={() => setShowJSON(expanded ? null : id)}
                          >
                            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                          onClick={() => onApply(action)}
                          disabled={busy || applyAllBusy || !action.endpoint}
                        >
                          Apply
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {expanded && (
                    <TableRow>
                      <TableCell colSpan={columns.length + 1} sx={{ bgcolor: 'background.default' }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            <strong>{action.method || 'POST'}</strong> {action.endpoint || '—'}
                          </Typography>
                          <Box
                            component="pre"
                            sx={{
                              m: 0,
                              p: 1,
                              fontSize: 11,
                              bgcolor: 'background.paper',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              overflow: 'auto',
                              maxHeight: 220,
                            }}
                          >
                            {JSON.stringify(action.payload || {}, null, 2)}
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ActionsTable;
